import { model as config, remote } from '@/config'
import {
  createEvent,
  createStore,
  sample,
  type Store,
  type StoreWritable,
} from 'effector'
import { appVersion } from '~/app/environment'
import * as runtime from '~/app/environment/runtime'
import { session } from '~/features/session'
import type { AnalytixParams } from '~/shared/analytix'
import { bannerEventsAnalyticsModel } from './banner'
import { epgEventsAnalyticsModel } from './epg'
import { favoriteEventsAnalyticsModel } from './favorite'
import { pageOpenEventsAnalyticsModel } from './page_open'
import { playerEventsAnalyticsModel } from './player'
import { searchEventsAnalyticsModel } from './search'
import { sessionEventsAnalyticsModel } from './session'
import { signUpEventsAnalyticsModel } from './sign_up'
import { disable, enable, initialize, initializeFx } from './wrapper'

/** Listener credentials (same contract as STPUVOAnalytix.initialize). */
const ANALYTIX_USER = 'devices'
const ANALYTIX_PASSWORD = 'Mn94PCq35ieg0uYOGIpFoSoGrCoRQDYLkJneMdcUcz'
const ANALYTIX_CONFIG_ACCOUNT_CODE = 'v.2.10'

/**
 * Process bootstrap trigger.
 */
export const init = createEvent()

export const $domain: Store<string> = config.get(
  remote.uvo_analyticsListenerDomainGo
)

export const $analytixCanBeInitialized: StoreWritable<boolean | null> =
  createStore<boolean | null>(null)

sample({
  clock: config.$ready,
  source: $domain,
  fn: (domain) => Boolean(domain),
  target: $analytixCanBeInitialized,
})

export const $eventCanBeSent = sessionEventsAnalyticsModel.$readyAllInfo

sample({
  clock: $analytixCanBeInitialized,
  source: {
    enabled: $analytixCanBeInitialized,
    domain: $domain,
  },
  filter: ({ enabled }) => Boolean(enabled),
  fn: ({ domain }): AnalytixParams => {
    return {
      baseUrl: domain,
      user: ANALYTIX_USER,
      password: ANALYTIX_PASSWORD,
      configAccountCode: ANALYTIX_CONFIG_ACCOUNT_CODE,
      contentProviderId: 'uvo',
      appName: runtime.app.name,
      appId: runtime.app.domain,
      appReleaseVersion: appVersion,
      deviceUserInfo: {
        deviceOsVersion: runtime.browser.version,
        deviceModel: runtime.browser.name,
        deviceName: runtime.os.name,
        deviceType: 'web',
        deviceId: runtime.ua.ua,
        deviceMacAddress: '',
        deviceSerialNumber: '',
      },
    }
  },
  target: initialize,
})

sample({
  clock: initializeFx.done,
  target: enable,
})

sample({
  clock: $analytixCanBeInitialized,
  filter: (enabled) => !enabled,
  target: disable,
})

// analytix events should update session
sample({
  clock: [
    sessionEventsAnalyticsModel.sendSessionStartFx,
    pageOpenEventsAnalyticsModel.sendPageOpenFx,
    favoriteEventsAnalyticsModel.sendFavoritesAddFx,
    favoriteEventsAnalyticsModel.sendFavoritesRemoveFx,
    searchEventsAnalyticsModel.sendSearchFx,
    playerEventsAnalyticsModel.sendPlayStartFx,
    playerEventsAnalyticsModel.sendPlayStopFx,
    playerEventsAnalyticsModel.sendPlayerOpenFx,
    playerEventsAnalyticsModel.sendPlayerWatchingFx,
    playerEventsAnalyticsModel.sendPlayerEventFx,
    playerEventsAnalyticsModel.sendPlayErrorFx,
    epgEventsAnalyticsModel.sendEpgViewFx,
    epgEventsAnalyticsModel.sendEpgCategoryFx,
    epgEventsAnalyticsModel.sendEpgChannelFx,
    epgEventsAnalyticsModel.sendEpgProgramFx,
    epgEventsAnalyticsModel.sendLiveModuleViewFx,
    bannerEventsAnalyticsModel.sendBannerImpressionFx,
    bannerEventsAnalyticsModel.sendBannerClickFx,
    bannerEventsAnalyticsModel.sendBannerDismissFx,
    signUpEventsAnalyticsModel.sendSignUpFx,
  ],
  target: session.updateSessionInfo,
})
